import { useState, type RefObject } from 'react';
import { useBag, useUseItem, type BagItemDto } from '../api/bag';
import { careFullReason, useCareAction } from '../api/care';
import type { CompanionDto } from '../api/companions';
import { Dialog } from '../components/BottomSheet';
import { Aside } from '../components/Aside';
import { itemIconPath } from '../compositor/assetPaths';
import { careItemError } from './FeedSheet';
import type { PlaceFoodItem } from './placeFood';
import { sfx } from './sfx';
import { WASH_STAGES } from './washStages';

export type CareTab = 'food' | 'groom' | 'other' | 'wash';

export type CareTrayProps = {
  companion: CompanionDto;
  onClose: () => void;
  onPlaceFood: (item: PlaceFoodItem) => void;
  onGroomPick: (itemCode: string) => void;
  onStageUp: (stage: string) => void;
  restoreFocusRef?: RefObject<HTMLElement | null>;
  portalContainer?: HTMLElement | null;
  variant?: 'sheet' | 'overlay';
  /** The tab to open on. The phone dock's Feed key opens on Food (the default); the desk's
   *  Dashboard opens its Groom card straight onto the Groom tab. */
  initialTab?: CareTab;
  /** Index into `WASH_STAGES` of the stage the wash is on (HabitatScreen's `wash.stageIndex`).
   *  Defaults to the first stage. A tool picked out of order is refused HERE, with the order
   *  said out loud — the shake path silently no-ops on it (QA 2026-09-23, battle F12). */
  washStageIndex?: number;
};

const TABS: { key: CareTab; label: string }[] = [
  { key: 'food', label: 'Food' },
  { key: 'groom', label: 'Groom' },
  { key: 'other', label: 'Other' },
  { key: 'wash', label: 'Wash' },
];

const EMPTY_COPY = 'Nothing to feed, groom, or play with yet — the Thatchwell market restocks all day.';

// The two `effectType`s `ItemUseService` refuses outside a battle (`battle_only`, QA 2026-09-23
// economy F3): shown, since they ARE in the bag, but never offered a Use here.
const BATTLE_EFFECTS = ['heal', 'cleanse'];
const BATTLE_ONLY_COPY = 'Only usable in battle';

// Trap #1 (task-16-brief.md): the three wash tools are a whole CATEGORY (owner decision D12,
// Task 13), not a subcategory like every other tab here — filtering on subcategory silently
// returns nothing.
function rowsFor(items: BagItemDto[], tab: CareTab): BagItemDto[] {
  return items.filter((i) => {
    if (!i.usable) return false;
    if (tab === 'food') return i.subcategory === 'food';
    if (tab === 'groom') return i.subcategory === 'grooming';
    if (tab === 'wash') return i.category === 'tool';
    return i.subcategory === 'toy' || i.subcategory === 'tonic';
  });
}

/**
 * SCREENS.md /habitat "care-tray": Food (place on floor), Groom (drag-and-shake over pet),
 * Other (toys + tonics, tap-to-use like FeedSheet). Food/groom pick never POSTs — the scene
 * owns place-food and groom-shake.
 */
export function CareTray({
  companion, onClose, onPlaceFood, onGroomPick, onStageUp, restoreFocusRef, portalContainer,
  variant = 'sheet', initialTab = 'food', washStageIndex = 0,
}: CareTrayProps) {
  const bag = useBag();
  const careAction = useCareAction(companion.id);
  const useItem = useUseItem();
  const [tab, setTab] = useState<CareTab>(initialTab);
  const [toast, setToast] = useState<string | null>(null);
  const [pendingCode, setPendingCode] = useState<string | null>(null);

  const items = bag.data?.items ?? [];
  const rows = rowsFor(items, tab);
  const hasAnyRows = (['food', 'groom', 'other'] as const).some((key) => rowsFor(items, key).length > 0);

  // Trap #3 (task-16-brief.md): always the three WASH_STAGES rows, in that fixed order — never
  // bag order, and never dependent on ownership (the player may not own all three; an unowned
  // row still renders, just unpickable). Trap #2: a wash tool's stage can't be read off the bag
  // row (`effectType` stays consumable-only), so the join key is the item CODE against
  // `WASH_STAGES`, not anything server-sent about stage.
  const ownedWashTools = rowsFor(items, 'wash');
  const washRows = WASH_STAGES.map((stage) => ({
    stage,
    item: ownedWashTools.find((r) => r.code === stage.itemCode) ?? null,
  }));
  const washMissingCount = washRows.filter((r) => r.item === null).length;

  function handleFoodPick(item: BagItemDto) {
    sfx.petFoodPick();
    onPlaceFood({ code: item.code, name: item.name });
    onClose();
  }

  function handleGroomPick(item: BagItemDto) {
    onGroomPick(item.code);
    onClose();
  }

  function handleWashPick(item: BagItemDto) {
    const next = WASH_STAGES[washStageIndex] ?? WASH_STAGES[0];
    if (item.code !== next.itemCode) {
      setToast(`${next.label} ${washStageIndex === 0 ? 'first' : 'next'} — water, then soap, then towel.`);
      return;
    }
    handleGroomPick(item);
  }

  async function handleToyRow(item: BagItemDto) {
    if (pendingCode) return;
    setPendingCode(item.code);
    try {
      await careAction.mutateAsync({ action: 'play', itemCode: item.code });
      setToast(`Played — ${item.name}`);
    } catch (err) {
      setToast(careItemError(err));
    } finally {
      setPendingCode(null);
    }
  }

  async function handleTonicRow(item: BagItemDto) {
    if (pendingCode) return;
    setPendingCode(item.code);
    try {
      const res = await useItem.mutateAsync({ itemCode: item.code, companionId: companion.id });
      onStageUp(res.result.stage);
      onClose();
    } catch (err) {
      setToast(careItemError(err));
      setPendingCode(null);
    }
  }

  return (
    <Aside
      title={`Feed ${companion.name}`}
      open
      onOpenChange={(next) => { if (!next) onClose(); }}
      contentClassName="feed-sheet care-tray"
      restoreFocusRef={restoreFocusRef}
      portalContainer={portalContainer}
      variant={variant}
    >
      <div className="sheet-header">
        <Dialog.Title asChild>
          <h2>Feed {companion.name}</h2>
        </Dialog.Title>
        <Dialog.Close asChild>
          <button type="button" className="sheet-close" aria-label="Close care tray">
            Close
          </button>
        </Dialog.Close>
      </div>
      <Dialog.Description className="sr-only">
        Food, grooming, toys, and tonics from your bag for {companion.name}.
      </Dialog.Description>

      <div className="sheet-body">
        <div className="care-tray-tabs" role="tablist" aria-label="Care">
          {TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={tab === t.key}
              className={tab === t.key ? 'chip chip-filter chip-active' : 'chip chip-filter'}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {bag.isLoading && <p className="form-note">Loading your bag…</p>}
        {bag.isError && (
          <div className="retry-card">
            <p>Couldn&rsquo;t reach Project Pets.</p>
            <button type="button" className="btn-primary" onClick={() => bag.refetch()}>Try again</button>
          </div>
        )}
        {/* The wash tab always has exactly the three WASH_STAGES rows — ownership never empties
            it — so it takes its own branch rather than the generic hasAnyRows/EMPTY_COPY path
            below, which is about a bag with nothing relevant in it at all. */}
        {!bag.isLoading && !bag.isError && tab === 'wash' && (
          <>
            <ul className="feed-sheet-list">
              {washRows.map(({ stage, item }) => {
                if (item) {
                  return (
                    <li key={stage.itemCode}>
                      <button type="button" className="feed-sheet-row care-tray-pick" onClick={() => handleWashPick(item)}>
                        <img src={itemIconPath(item.code)} alt="" className="feed-sheet-icon" />
                        <div className="feed-sheet-info">
                          <span className="feed-sheet-name" data-testid="wash-stage-name">{item.name}</span>
                          {item.description && <p className="feed-sheet-hint">{item.description}</p>}
                        </div>
                      </button>
                    </li>
                  );
                }
                return (
                  <li key={stage.itemCode} className="feed-sheet-row care-tray-row-unowned">
                    <img src={itemIconPath(stage.itemCode)} alt="" className="feed-sheet-icon" />
                    <div className="feed-sheet-info">
                      <span className="feed-sheet-name" data-testid="wash-stage-name">{stage.label}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
            {washMissingCount > 0 && <p className="form-note">Buy it in Hearthollow.</p>}
          </>
        )}

        {tab !== 'wash' && !bag.isLoading && !bag.isError && !hasAnyRows && <p className="form-note">{EMPTY_COPY}</p>}

        {tab !== 'wash' && !bag.isLoading && !bag.isError && hasAnyRows && rows.length === 0 && (
          <p className="form-note">Nothing in this tab right now.</p>
        )}

        {tab !== 'wash' && rows.length > 0 && (
          <ul className="feed-sheet-list">
            {rows.map((item) => {
              const busy = pendingCode === item.code;
              // A full stat says why on the hint line (economy F10) but the row stays pickable:
              // the Feed/Groom/Play dailies and the Settling In chain need the ACT, even when a
              // meter is already full (coordinator ruling, QA fix pass 2026-09-23).
              const fullReason = careFullReason(item, companion);
              if (tab === 'food' || tab === 'groom') {
                const pick = tab === 'food' ? handleFoodPick : handleGroomPick;
                return (
                  <li key={item.code}>
                    <button type="button" className="feed-sheet-row care-tray-pick" onClick={() => pick(item)}>
                      <img src={itemIconPath(item.code)} alt="" className="feed-sheet-icon" />
                      <div className="feed-sheet-info">
                        <span className="feed-sheet-name">{item.name}</span>
                        <span className="feed-sheet-qty">Qty: {item.qty}</span>
                        {fullReason
                          ? <p className="feed-sheet-hint">{fullReason}</p>
                          : item.description && <p className="feed-sheet-hint">{item.description}</p>}
                      </div>
                    </button>
                  </li>
                );
              }
              const isTonic = item.subcategory === 'tonic';
              const battleOnly = BATTLE_EFFECTS.includes(item.effectType ?? '');
              const reason = battleOnly ? BATTLE_ONLY_COPY : fullReason;
              const label = isTonic ? `Use ${item.name}` : 'Play';
              return (
                <li key={item.code} className="feed-sheet-row">
                  <img src={itemIconPath(item.code)} alt="" className="feed-sheet-icon" />
                  <div className="feed-sheet-info">
                    <span className="feed-sheet-name">{item.name}</span>
                    <span className="feed-sheet-qty">Qty: {item.qty}</span>
                    {reason
                      ? <p className="feed-sheet-hint">{reason}</p>
                      : item.description && <p className="feed-sheet-hint">{item.description}</p>}
                  </div>
                  <button
                    type="button"
                    className="btn-quiet"
                    disabled={busy || battleOnly}
                    onClick={() => (isTonic ? handleTonicRow(item) : handleToyRow(item))}
                  >
                    {busy ? 'Working…' : label}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        {toast && (
          <div className="toast" role="alert">
            <span>{toast}</span>
            <button type="button" className="toast-dismiss" onClick={() => setToast(null)}>Dismiss</button>
          </div>
        )}
      </div>
    </Aside>
  );
}
